import React, { useEffect, useRef, useState } from "react";
import { connect } from 'react-redux';
import Modal from "@material-ui/core/Modal";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputField from "../../_shared/input-field/InputField";
import FormControl from "@material-ui/core/FormControl";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import Close from "@material-ui/icons/Close";
import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import RawTool from '@editorjs/raw';
import DOMPurify from "dompurify";
import { recipientsList, emailNotificationSendStatus } from "../../project/projectSelectors";
import PropTypes from "prop-types";
import "./ProjectAdHocEmailNotification.scss";
import { sendEmailNotification, resetEmailNotificationSendStatus } from "../../project/projectActions";
import isEmpty from "lodash/isEmpty";

const escapeHtml = (value) => {
  const text = String(value ?? "");
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replaceAll(/&nbsp;|&#160;|&#xa0;|&amp;nbsp;/g, "")

};

const sanitizeHtml = (html) => {
  return DOMPurify.sanitize(html || "", {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ["script", "style", "iframe", "object", "embed", "link", "meta", "base"],
  });
};

const hasMeaningfulContent = (html) => {
  if (!html) return false;
  const textOnly = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;|&#160;|&#xa0;/g, "")
    .replace(/\u00a0/g, " ")
    .trim();
  return textOnly.length > 0;
};

const convertEditorJsToHtml = (editorData) => {
  if (!editorData?.blocks) return '';
  const rawHtml = editorData.blocks.map(block => {
    switch (block.type) {
      case 'header':
        return `<h${block.data.level}>${escapeHtml(block.data.text)}</h${block.data.level}>`;
      case 'paragraph':
        return `<p>${escapeHtml(block.data.text)}</p>`;
      case 'list': {
        const items = (block.data.items || []).map(item => `<li>${escapeHtml(item)}</li>`).join('');
        const tag = block.data.style === 'ordered' ? 'ol' : 'ul';
        return `<${tag}>${items}</${tag}>`;
      }
      case 'raw':
        return sanitizeHtml(block.data.html || '');
      default:
        return `<p>${escapeHtml(block.data.text || '')}</p>`;
    }
  }).join('');

  return sanitizeHtml(rawHtml);
};


const getRoleName = (roleCode) => {
  if (roleCode != null) {
    switch (roleCode.trim()) {
      case 'ACCOUNTANT_ROLE':
        return 'Accountant';
      case 'OUTSIDE_COUNCIL_ROLE':
        return 'Outside Counsel';
      case 'REGIONAL_VICE_PRESIDENT_ROLE':
        return 'Regional Vice President';
      case 'AUDITOR_ROLE':
        return 'Auditor';
      case 'PARALEGAL_ROLE':
        return 'Paralegal';
      case 'REAL_ESTATE_MANAGER_ROLE':
        return 'Real Estate Manager';
      case 'GENERAL_COUNCIL_ROLE':
        return 'General Counsel';
      case 'DISTRICT_MANAGER_ROLE':
        return 'District Manager';
      case 'ADMIN_ROLE':
        return 'Admin';
      case 'POST_CLOSING_ROLE':
        return 'Post Closing';
      case 'MEMBER_ROLE':
        return 'Member';
      case 'PLADMIN_ROLE':
        return 'PL Admin';
      case 'AUADMIN_ROLE':
        return 'AU Admin';
      case 'SUPER_USER_ROLE':
        return 'Super User';
      case 'CIVIL_ENGINEER_ROLE':
        return 'Civil Engineer';
      case 'REL_ADMIN_ROLE':
        return 'REL Admin';
      default:
        return '';
    }
  }
  return '';
}

const ProjectAdHocEmailNotification = ({
  open,
  onClose,
  users,
  closing_id,
  sendEmailNotification,
  emailSendStatus,
  resetEmailNotificationSendStatus,
}) => {
  const editorInstance = useRef(null);
  const hasSubmittedRef = useRef(false);
  const [selectedRecipients, setSelectedRecipients] = useState(users);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState(null);
  const [errors, setErrors] = useState({ recipients: '', subject: '', content: '' });

  const updateRecipients = (nextRecipients) => {
    setSelectedRecipients(nextRecipients);
    if (nextRecipients.length > 0) {
      setErrors(prev => ({ ...prev, recipients: '' }));
    }
  };

  const handleRecipientToggle = (user, checked) => {
    const nextRecipients = checked
      ? [...selectedRecipients, user]
      : selectedRecipients.filter(selected => selected.ext_email_addr_id !== user.ext_email_addr_id);

    updateRecipients(nextRecipients);
  };

  const handleSelectAllChange = (checked) => {
    updateRecipients(checked ? users : []);
  };

  useEffect(() => {
    if (open) {
      if (!editorInstance.current) {
        editorInstance.current = new EditorJS({
          holder: 'editorjs', // The ID of the container element
          tools: {
            raw: RawTool,
            header: { class: Header, inlineToolbar: false, config: { placeholder: 'Enter a header' } },
            list: { class: List, inlineToolbar: false, config: { placeholder: 'Enter a list item' } },
          },
          placeholder: 'Let`s write an awesome story!',
          onChange: async () => {
            try {
              const content = await editorInstance.current.save();
              setEmailContent(content);
              setErrors(prev => ({ ...prev, content: '' }));
            } catch (error) {
              setErrors(prev => ({
                ...prev,
                content: 'Unable to read email content. Please try again.',
              }));
            }
          }
        });
      }

      // Cleanup: Destroy editor on unmount
      return () => {
        if (editorInstance.current && typeof editorInstance.current.destroy === 'function') {
          editorInstance.current.destroy();
          editorInstance.current = null;
        }
      };
    }
  }, [open]);

  useEffect(() => {
    setSelectedRecipients(users || []);
  }, [users]);

  useEffect(() => {
    if (!open) {
      hasSubmittedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    const hasTerminalState = Boolean(emailSendStatus.response) || Boolean(emailSendStatus.error);

    if (!open || !hasSubmittedRef.current || emailSendStatus.loading || !hasTerminalState) {
      return;
    }

    hasSubmittedRef.current = false;
    onClose();
    resetEmailNotificationSendStatus();
  }, [
    emailSendStatus.error,
    emailSendStatus.loading,
    emailSendStatus.response,
    onClose,
    open,
    resetEmailNotificationSendStatus,
  ]);

  const handleSendEmail = async () => {
    const newErrors = { recipients: '', subject: '', content: '' };
    let hasError = false;

    if (selectedRecipients.length === 0) {
      newErrors.recipients = 'Please select at least one recipient.';
      hasError = true;
    }
    if (!emailSubject || emailSubject.trim().length === 0) {
      newErrors.subject = 'Please enter an email subject.';
      hasError = true;
    }
    let content = emailContent;
    if (editorInstance.current) {
      try {
        content = await editorInstance.current.save();
      } catch (error) {
        newErrors.content = 'Unable to read email content. Please try again.';
        hasError = true;
      }
    }

    if ((!content || !content.blocks || content.blocks.length === 0) && !newErrors.content) {
      newErrors.content = 'Please enter email content.';
      hasError = true;
    }

    setErrors(newErrors);
    if (hasError) return;

    const htmlContent = convertEditorJsToHtml(content);
    if (!hasMeaningfulContent(htmlContent)) {
      setErrors(prev => ({
        ...prev,
        content: 'Email content contains unsupported or unsafe markup.',
      }));
      return;
    }

    const recipientData = selectedRecipients.map(user => ({ email: user.ext_email_addr_id, userid: user.sys_userid }));
    hasSubmittedRef.current = true;
    sendEmailNotification(closing_id, recipientData, emailSubject, htmlContent);
  }
  if (!open) return null;
  return (
    <Modal open={open} className="adhoc-email-notification-modal">
      <div className="modal-body">
        <div className="header">
          <Typography variant="h5">Email Notification</Typography>
          <IconButton className="icon-button" onClick={onClose}>
            <Close />
          </IconButton>
        </div>
        <div className="body">
          <FormControl component="fieldset" className="input-field">
            <FormLabel variant="caption" style={{ fontSize: '0.75rem' }}>Recipients</FormLabel>
            {isEmpty(users) ? <Typography variant="caption" color="error">No recipients available.</Typography> : <FormGroup row={true}>
              {users.map(user => (
                <FormControlLabel
                  key={user.ext_email_addr_id}
                  control={
                    <Checkbox
                      checked={selectedRecipients.some(selected => selected.ext_email_addr_id === user.ext_email_addr_id)}
                      onChange={(event) => handleRecipientToggle(user, event.target.checked)}
                      name={user.fullname}
                    />
                  }
                  label={`${user.fullname} (${getRoleName(user?.role_desc) || user.role_desc})`}
                />
              ))}
              <FormControlLabel
                key={"Select All"}
                control={
                  <Checkbox
                    checked={users.length > 0 && selectedRecipients.length === users.length}
                    onChange={(event) => handleSelectAllChange(event.target.checked)}
                    name={"Select All"}
                  />
                }
                label={`Select All`}
              />
            </FormGroup>}
            {errors.recipients && <Typography variant="caption" color="error">{errors.recipients}</Typography>}
          </FormControl>
          <InputField
            inputLabel="Subject"
            placeholder="Enter email subject"
            inputStyleType={"sizeFull"}
            value={emailSubject}
            onChange={e => {
              setEmailSubject(e.target.value);
              if (e.target.value.trim().length > 0) {
                setErrors(prev => ({ ...prev, subject: '' }));
              }
            }}
            error={!!errors.subject}
            helperText={errors.subject}
          />
          <div className="input-field">
            <Typography variant="caption" style={{ color: "rgba(0, 0, 0, 0.54)" }} gutterBottom>
              Email Content
              <br />
            </Typography>
            <div id="editorjs" style={{ border: errors.content ? '1px solid #f44336' : '1px solid #ccc', padding: '10px' }} />
            {errors.content && <Typography variant="caption" color="error">{errors.content}</Typography>}
          </div>
        </div>
        <div className="footer">
          <Button variant="outlined" color="primary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSendEmail} disabled={emailSendStatus.loading}>
            Send Email
          </Button>
        </div>
      </div>
    </Modal>
  );
}

ProjectAdHocEmailNotification.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired,
  closing_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sendEmailNotification: PropTypes.func.isRequired,
  resetEmailNotificationSendStatus: PropTypes.func.isRequired,
  emailSendStatus: PropTypes.shape({
    loading: PropTypes.bool,
    response: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  }).isRequired,
};

const mapStateToProps = (state, ownProps) => {
  return ({
    users: recipientsList(state) ?? [],
    emailSendStatus: emailNotificationSendStatus(state),
  });
};
const mapDispatchToProps = dispatch => ({
  sendEmailNotification: (closing_id, recipients, subject, htmlContent) => dispatch(sendEmailNotification(closing_id, recipients, subject, htmlContent)),
  resetEmailNotificationSendStatus: () => dispatch(resetEmailNotificationSendStatus()),
});
export default connect(mapStateToProps, mapDispatchToProps)(ProjectAdHocEmailNotification);